import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { FrontendLoggerService } from './frontend-logger.service';
import type { Notification } from './notification.service';

export interface NotificationReceiver {
  receive(notification: Notification): void;
}

export class NotificationStateStore implements NotificationReceiver {
  private readonly notifications = new BehaviorSubject<Notification[]>([]);
  private readonly transient = new Subject<Notification>();
  private readonly unreadCount = new BehaviorSubject<number>(0);

  readonly notifications$ = this.notifications.asObservable();
  readonly transient$ = this.transient.asObservable();
  readonly unreadCount$ = this.unreadCount.asObservable();

  constructor(
    private readonly http: HttpClient,
    private readonly apiUrl: string,
    private readonly logger: FrontendLoggerService
  ) {}

  showTransient(message: string, type: Notification['type'] = 'info'): void {
    this.transient.next(this.createNotification({ message, type }));
  }

  pushLocal(
    type: Notification['type'],
    message: string,
    data?: Record<string, unknown>
  ): void {
    const notification = this.createNotification({ type, message, data });
    this.receive(notification);
  }

  receive(notification: Notification): void {
    this.notifications.next([notification, ...this.notifications.value]);
    this.updateUnreadCount();
    try {
      this.transient.next(notification);
    } catch (error) {
      this.logger.error(
        '[NotificationService] Failed to emit transient notification: ' + String(error)
      );
    }
  }

  loadNotifications(): void {
    this.getNotifications().subscribe({
      next: (data) => {
        const notifications = data.map((item) => ({
          ...item,
          timestamp: new Date(item.timestamp || new Date()),
          read: item.read || false,
        }));
        this.notifications.next(notifications);
        this.updateUnreadCount();
      },
      error: (error) => this.logger.error('Error loading notifications', error),
    });
  }

  updateUnreadCount(): void {
    this.unreadCount.next(this.notifications.value.filter((item) => !item.read).length);
  }

  getNotifications(): Observable<any[]> {
    return this.http.get<any[]>(`${this.apiUrl}/notifications`);
  }

  markAsRead(id: string): void {
    this.notifications.next(
      this.notifications.value.map((notification) =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
    this.updateUnreadCount();
  }

  markAllAsRead(): void {
    this.notifications.next(
      this.notifications.value.map((notification) => ({ ...notification, read: true }))
    );
    this.updateUnreadCount();
  }

  addNotification(notification: Partial<Notification>): void {
    const newNotification = this.createNotification(notification);
    this.notifications.next([newNotification, ...this.notifications.value]);
    this.updateUnreadCount();
  }

  private createNotification(notification: Partial<Notification>): Notification {
    return {
      id: Math.random().toString(36).slice(2, 11),
      type: notification.type || 'info',
      message: notification.message || '',
      priority: notification.priority || 'medium',
      timestamp: new Date(),
      read: false,
      ...(notification.data ? { data: notification.data } : {}),
    };
  }
}
